<template>
  <v-container grid-list-md fluid>
    <v-layout wrap justify-space-between>
      <v-flex xs12 sm2>
        <v-btn
          color="primary"
          @click="create"
          class="mb-4"
          :disabled="!hasCondominioAccess(entity, 'create')"
        >
          {{ this.translate("system.button.add.title") }}
          <v-icon dark>{{ this.translate("system.button.add.icon") }}</v-icon>
        </v-btn>
      </v-flex>
      <v-flex xs12 sm4>
        <v-text-field
          append-icon="search"
          label="Pesquisar"
          v-model="search"
          @change="updateSum"
        ></v-text-field>
      </v-flex>
      <v-flex xs12 sm1>
        <v-menu offset-y>
          <v-btn slot="activator" icon class="ml-2" title="Relatórios">
            <v-icon>description</v-icon>
          </v-btn>
          <v-list>
            <!-- PDF -->
            <v-list-tile @click="exportReport(1)">
              <v-list-tile-action>
                <v-icon color="red darken-1">picture_as_pdf</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>Relatório em PDF</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <!-- Excel -->
            <v-list-tile @click="exportReport(2)">
              <v-list-tile-action>
                <v-icon color="green darken-2">table_chart</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>Relatório em Excel</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-menu>
      </v-flex>

      <!--Index-->
      <v-flex xs12>
        <v-data-table
          ref="RendimentoAcumuladamenteTable"
          :headers="headers"
          :items="items"
          :search="search"
          class="elevation-1"
          :rows-per-page-items="[100, 50, 25, { text: 'Todos', value: -1 }]"
          rows-per-page-text="Registros por página"
          no-data-text="Não existem registros"
          no-results-text="Nenhum registro encontrado"
        >
          <template slot="items" slot-scope="props">
            <td>{{ getFormaTributacaoText(props.item.forma_tributacao) }}</td>
            <td>{{ props.item.nome_fonte_pagadora }}</td>
            <td>{{ props.item.cpf_cnpj_fonte_pagadora | formatCpfCnpj }}</td>
            <td align="center">{{ getMesText(props.item.mes_recebimento) }}</td>
            <td align="center">{{ props.item.numero_meses }}</td>
            <td align="right">{{ props.item.rendimentos_tributaveis | formatCurrency }}</td>
            <td align="right">{{ props.item.parcela_isenta_65_anos | formatCurrency }}</td>
            <td align="right">{{ props.item.contribuicao_previdenciaria_oficial | formatCurrency }}</td>
            <td align="right">{{ props.item.imposto_retido_fonte | formatCurrency }}</td>
            <td align="right">{{ props.item.valor_recebido_juros | formatCurrency }}</td>
            <td align="right">{{ props.item.total_rendimentos | formatCurrency }}</td>
            <td>{{ props.item.dependente ? props.item.dependente.id_texto : "" }}</td>
            <td align="center" style="min-width: 150px">
              <v-btn
                icon
                class="mx-0"
                @click="editItem(props.item)"
                :disabled="!hasCondominioAccess(entity, 'update')"
              >
                <v-icon color="teal">edit</v-icon>
              </v-btn>
              <v-btn
                icon
                class="mx-0"
                @click="deleteItem(props.item)"
                :disabled="!hasCondominioAccess(entity, 'delete')"
              >
                <v-icon color="pink">delete</v-icon>
              </v-btn>
            </td>
          </template>
          <template v-slot:footer>
            <td colspan="5">
              <strong>Total Geral</strong>
            </td>
            <td align="right">
              <strong>{{ sum.rendimentos_tributaveis | formatCurrency }}</strong>
            </td>
            <td align="right">
              <strong>{{ sum.parcela_isenta_65_anos | formatCurrency }}</strong>
            </td>
            <td align="right">
              <strong>{{ sum.contribuicao_previdenciaria_oficial | formatCurrency }}</strong>
            </td>
            <td align="right">
              <strong>{{ sum.imposto_retido_fonte | formatCurrency }}</strong>
            </td>
            <td align="right">
              <strong>{{ sum.valor_recebido_juros | formatCurrency }}</strong>
            </td>
            <td align="right">
              <strong>{{ sum.total_rendimentos | formatCurrency }}</strong>
            </td>
            <td colspan="2"></td>
          </template>
          <template slot="pageText" slot-scope="props">
            {{ props.pageStart }} - {{ props.pageStop }} de {{ props.itemsLength }}
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <!--Create Edit-->
    <v-dialog v-model="dialog" max-width="1200px" persistent>
      <v-card>
        <v-card-text>
          <v-container grid-list-md fluid>
            <v-layout wrap>
              <v-flex xs12>
                <span class="display-1">{{ formTitle }}</span>
              </v-flex>

              <v-flex xs12>
                <v-radio-group
                  v-model="editedItem.forma_tributacao"
                  name="forma_tributacao"
                  v-validate="'required'"
                  :error-messages="errors.collect('forma_tributacao')"
                  :data-vv-as="translate('table.' + entity + '.field.forma_tributacao')"
                  row
                >
                  <v-radio
                    v-for="opt in formaTributacaoOptions"
                    :key="opt.value"
                    :label="opt.text"
                    :value="opt.value"
                  ></v-radio>
                </v-radio-group>
              </v-flex>

              <v-flex xs12 md6>
                <p-cpf-cnpj
                  name="cpf_cnpj_fonte_pagadora"
                  v-model="editedItem.cpf_cnpj_fonte_pagadora"
                  v_validate="required"
                  :label="translate('table.' + entity + '.field.cpf_cnpj_fonte_pagadora')"
                ></p-cpf-cnpj>
              </v-flex>

              <v-flex xs12 md6>
                <v-text-field
                  name="nome_fonte_pagadora"
                  v-model="editedItem.nome_fonte_pagadora"
                  counter
                  maxlength="60"
                  v-validate="'required'"
                  data-vv-validate-on="blur"
                  :error-messages="errors.collect('nome_fonte_pagadora')"
                  :data-vv-as="translate('table.' + entity + '.field.nome_fonte_pagadora')"
                  :label="translate('table.' + entity + '.field.nome_fonte_pagadora')"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 md3>
                <v-select
                  name="mes_recebimento"
                  v-model="editedItem.mes_recebimento"
                  :items="mesesOptions"
                  :label="translate('table.' + entity + '.field.mes_recebimento')"
                ></v-select>
              </v-flex>
              
              <v-flex xs12 md3>
                <v-text-field
                  name="numero_meses"
                  v-model="editedItem.numero_meses"
                  type="number"
                  min="1"
                  max="12"
                  :label="translate('table.' + entity + '.field.numero_meses')"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 md3>
                <p-money
                  name="rendimentos_tributaveis"
                  v-model="editedItem.rendimentos_tributaveis"
                  :label="translate('table.' + entity + '.field.rendimentos_tributaveis')"
                  :v_validate="'positiveNumber'"
                ></p-money>
              </v-flex>

              <v-flex xs12 md3>
                <p-money
                  name="contribuicao_previdenciaria_oficial"
                  v-model="editedItem.contribuicao_previdenciaria_oficial"
                  :label="translate('table.' + entity + '.field.contribuicao_previdenciaria_oficial')"
                  :v_validate="'positiveNumber'"
                ></p-money>
              </v-flex>

              <v-flex xs12 md3>
                <p-money
                  name="parcela_isenta_65_anos"
                  v-model="editedItem.parcela_isenta_65_anos"
                  :label="translate('table.' + entity + '.field.parcela_isenta_65_anos')"
                  :v_validate="'positiveNumber'"
                ></p-money>
              </v-flex>

              <v-flex xs12 md3>
                <p-money
                  name="imposto_retido_fonte"
                  v-model="editedItem.imposto_retido_fonte"
                  :label="translate('table.' + entity + '.field.imposto_retido_fonte')"
                  :v_validate="'positiveNumber'"
                ></p-money>
              </v-flex>

              <v-flex xs12 md3>
                <p-money
                  name="valor_recebido_juros"
                  v-model="editedItem.valor_recebido_juros"
                  :label="translate('table.' + entity + '.field.valor_recebido_juros')"
                  :v_validate="'positiveNumber'"
                ></p-money>
              </v-flex>

              <v-flex xs12 md3>
                <v-layout align-center>
                  <v-flex>
                    <p-money
                      name="total_rendimentos"
                      :value="totalRendimentos"
                      :label="translate('table.' + entity + '.field.total_rendimentos')"
                      disabled
                    ></p-money>
                  </v-flex>
                  <v-flex shrink>
                    <v-tooltip bottom max-width="420">
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" color="info" class="ml-1">info</v-icon>
                      </template>
                      <span>
                        A isenção dos proventos de aposentadoria, reserva remunerada, reforma e pensão de declarante com 65 anos ou mais, até o limite da primeira faixa da tabela mensal, aplica-se apenas quando o contribuinte optar pela forma de tributação <strong>Ajuste Anual</strong> para os rendimentos recebidos acumuladamente. Ao marcar a forma de tributação <strong>Exclusiva de Fonte</strong>, o valor informado em "Parcela Isenta 65 anos" será somado a "Rendimentos tributáveis".
                      </span>
                    </v-tooltip>
                  </v-flex>
                </v-layout>
              </v-flex>
              
              <v-flex xs12>
                <v-autocomplete
                  :label="translate('table.' + entity + '.field.pessoa_dependente_id')"
                  v-model="editedItem.pessoa_dependente_id"
                  :items="dependente_items"
                  item-text="id_texto"
                  item-value="id"
                  clearable
                ></v-autocomplete>
              </v-flex>

              <v-flex xs12>
                <v-btn class="mx-1" color="primary darken-2" :disabled="saving" :loading="saving" @click.native="save()">Salvar</v-btn>
                <v-btn class="mx-1" @click.native="close(400)">Cancelar</v-btn>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import crud from "./../../mixin/crud";
import PMoney from "../shared/PMoney";
import acl from "./../../mixin/acl";
import PCpfCnpj from "../shared/PCpfCnpj";

export default {
  components: {
    PMoney,
    PCpfCnpj,
  },
  mixins: [crud, acl],
  data: () => ({
    entity: "condominio_rendimentoacumuladamente",
    items: [],
    sum: {
      rendimentos_tributaveis: 0,
      imposto_retido_fonte: 0,
    },
    formaTributacaoOptions: [
      { value: 1, text: "Ajuste Anual" },
      { value: 2, text: "Exclusivamente na Fonte" },
    ],
    mesesOptions: [
      { value: 1,  text: "Janeiro" },
      { value: 2,  text: "Fevereiro" },
      { value: 3,  text: "Março" },
      { value: 4,  text: "Abril" },
      { value: 5,  text: "Maio" },
      { value: 6,  text: "Junho" },
      { value: 7,  text: "Julho" },
      { value: 8,  text: "Agosto" },
      { value: 9,  text: "Setembro" },
      { value: 10, text: "Outubro" },
      { value: 11, text: "Novembro" },
      { value: 12, text: "Dezembro" },
    ],
  }),
  name: "RendimentoAcumuladamente",
  props: ["condominio_id", "anobase_id", "values", "dependente_items"],
  created() {
    this.items = this.values;
    this.editedItem = this.buildDefault();
    this.defaultItem  = this.buildDefault();
  },
  watch: {
    items() {
      this.updateSum();
    },
  },
  computed: {
    totalRendimentos() {
      const rend   = parseFloat(this.editedItem.rendimentos_tributaveis) || 0;
      const isenta = parseFloat(this.editedItem.parcela_isenta_65_anos)  || 0;
      return this.editedItem.forma_tributacao === 2 ? rend + isenta : rend;
    },
    headers() {
      return [
        {
          text: this.translate("table." + this.entity + ".field.forma_tributacao"),
          value: "forma_tributacao",
        },
        {
          text: this.translate("table." + this.entity + ".field.nome_fonte_pagadora"),
          value: "nome_fonte_pagadora",
        },
        {
          text: this.translate("table." + this.entity + ".field.cpf_cnpj_fonte_pagadora"),
          value: "cpf_cnpj_fonte_pagadora",
        },
        {
          text: this.translate("table." + this.entity + ".field.mes_recebimento"),
          value: "mes_recebimento",
          sortable: false,
          align: "center",
        },
        {
          text: this.translate("table." + this.entity + ".field.numero_meses"),
          value: "numero_meses",
          sortable: false,
          align: "center",
        },
        {
          text: this.translate("table." + this.entity + ".field.rendimentos_tributaveis"),
          value: "rendimentos_tributaveis",
          sortable: false,
          align: "right",
        },
        {
          text: this.translate("table." + this.entity + ".field.parcela_isenta_65_anos"),
          value: "parcela_isenta_65_anos",
          sortable: false,
          align: "right",
        },
        {
          text: this.translate("table." + this.entity + ".field.contribuicao_previdenciaria_oficial"),
          value: "contribuicao_previdenciaria_oficial",
          sortable: false,
          align: "right",
        },
        {
          text: this.translate("table." + this.entity + ".field.imposto_retido_fonte"),
          value: "imposto_retido_fonte",
          sortable: false,
          align: "right",
        },
        {
          text: this.translate("table." + this.entity + ".field.valor_recebido_juros"),
          value: "valor_recebido_juros",
          sortable: false,
          align: "right",
        },
        {
          text: this.translate("table." + this.entity + ".field.total_rendimentos"),
          value: "total_rendimentos",
          sortable: false,
          align: "right",
        },
        {
          text: this.translate("table." + this.entity + ".field.pessoa_dependente_id"),
          value: "dependente.id_texto",
        },
        {
          text: this.translate("system.table.action"),
          value: "action",
          sortable: false,
          align: "center",
          width: "10%",
        },
      ];
    },
  },
  methods: {
    buildDefault() {
      return {
        condominio_id: this.condominio_id,
        anobase_id: this.anobase_id,
        registro_tipo: 1,
        forma_tributacao: null,
        cpf_cnpj_fonte_pagadora: "",
        nome_fonte_pagadora: "",
        rendimentos_tributaveis: 0,
        parcela_isenta_65_anos: 0,
        total_rendimentos: 0,
        valor_recebido_juros: 0,
        contribuicao_previdenciaria_oficial: 0,
        imposto_retido_fonte: 0,
        mes_recebimento: null,
        numero_meses: null,
        pessoa_dependente_id: null,
      };
    },
    getFormaTributacaoText(value) {
      const opt = this.formaTributacaoOptions.find(o => o.value === value);
      return opt ? opt.text : value;
    },
    getMesText(value) {
      const opt = this.mesesOptions.find(o => o.value === value);
      return opt ? opt.text : (value || "");
    },
    updateSum() {
      const table = this.$refs.RendimentoAcumuladamenteTable;
      if (!table) return;
      this.sum = table.filteredItems.reduce(
        (acc, item) => {
          acc.rendimentos_tributaveis          += item.rendimentos_tributaveis          || 0;
          acc.parcela_isenta_65_anos           += item.parcela_isenta_65_anos           || 0;
          acc.contribuicao_previdenciaria_oficial += item.contribuicao_previdenciaria_oficial || 0;
          acc.imposto_retido_fonte             += item.imposto_retido_fonte             || 0;
          acc.valor_recebido_juros             += item.valor_recebido_juros             || 0;
          acc.total_rendimentos                += item.total_rendimentos                || 0;
          return acc;
        },
        {
          rendimentos_tributaveis: 0,
          parcela_isenta_65_anos: 0,
          contribuicao_previdenciaria_oficial: 0,
          imposto_retido_fonte: 0,
          valor_recebido_juros: 0,
          total_rendimentos: 0,
        }
      );
    },
    beforeSave() {
      this.editedItem.registro_tipo    = this.editedItem.pessoa_dependente_id ? 2 : 1;
      this.editedItem.total_rendimentos = this.totalRendimentos;
    },
    // type 1 = pdf 2 = excel
    exportReport(type) {
      this.$store.commit("loader/change_dialog", true);
      this.$http.put(`report/report_details/${this.condominio_id}`, { "type": type, "table": "condominio_rendimentoacumuladamente", "search": this.search }).then(
        (res) => {
          let fileName = res.data;
          window.open(process.env.API + "reports/" + fileName, "_blank");
          this.$store.commit("loader/change_dialog", false);
          setTimeout(function () {
            _this.$http.delete(`report/delete_upload_report/${fileName}`)
          }, 5000)
        },
        (error) => {
          this.$store.commit("loader/change_dialog", false);
          this.$store.commit("snackbar/change_values", {
            text:
              error.body.message ||
              "Desculpe, não conseguimos gerar o relatório no momento. Por favor, tente novamente mais tarde.",
            color: "error",
            dialog: true,
          });
        }
      );
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/details/RendimentoAcumuladamente.vue